import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";

interface Props {
  children: React.ReactNode;
  content: React.ReactNode;
  direction?: "top" | "bottom";
  align?: "left" | "center" | "right";
  className?: string;
}

const ARROW_SIZE = 6;
const GAP = 8;
const ARROW_MIN_OFFSET = 16;

export default function OnboardingTooltip({ children, content, direction = "bottom", align = "center", className }: Props) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    arrowLeft: number;
    effectiveDirection: "top" | "bottom";
  } | null>(null);
  const [isPositioned, setIsPositioned] = useState(false);

  function clearCloseTimer() {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function handleMouseEnter() {
    clearCloseTimer();
    setOpen(true);
  }

  function handleMouseLeave() {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), 100);
  }

  const updatePosition = useCallback(
    function updatePosition() {
      if (!triggerRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current?.getBoundingClientRect();
      const tooltipHeight = tooltipRect?.height || 0;
      const tooltipWidth = tooltipRect?.width || 0;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const spaceBelow = viewportHeight - triggerRect.bottom - GAP;
      const spaceAbove = triggerRect.top - GAP;
      const effectiveDirection =
        direction === "top"
          ? spaceAbove >= tooltipHeight
            ? "top"
            : "bottom"
          : spaceBelow >= tooltipHeight
            ? "bottom"
            : "top";

      let top = 0;
      if (effectiveDirection === "bottom") {
        top = triggerRect.bottom + GAP + ARROW_SIZE;
      } else {
        top = triggerRect.top - tooltipHeight - GAP - ARROW_SIZE;
      }

      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      let left = 0;

      if (align === "center") {
        left = triggerCenter - tooltipWidth / 2;
      } else if (align === "left") {
        left = triggerRect.left;
      } else {
        left = triggerRect.right - tooltipWidth;
      }

      left = Math.max(8, Math.min(left, viewportWidth - tooltipWidth - 8));
      top = Math.max(8, Math.min(top, viewportHeight - tooltipHeight - 8));

      let arrowLeft = triggerCenter - left;
      const maxArrow = tooltipWidth > 0 ? tooltipWidth - ARROW_MIN_OFFSET : ARROW_MIN_OFFSET;
      arrowLeft = Math.max(ARROW_MIN_OFFSET, Math.min(arrowLeft, maxArrow));

      setPosition({ top, left, arrowLeft, effectiveDirection });
    },
    [direction, align],
  );

  useEffect(
    function positionEffect() {
      if (!open) {
        setPosition(null);
        setIsPositioned(false);
        return;
      }

      let aborted = false;
      let innerRafId: number | undefined;

      updatePosition();

      const outerRafId = requestAnimationFrame(() => {
        if (aborted) return;
        innerRafId = requestAnimationFrame(() => {
          if (aborted) return;
          updatePosition();
          setIsPositioned(true);
        });
      });

      function handleScrollOrResize() {
        updatePosition();
      }

      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);

      return () => {
        aborted = true;
        cancelAnimationFrame(outerRafId);
        if (innerRafId !== undefined) cancelAnimationFrame(innerRafId);
        setIsPositioned(false);
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    },
    [open, updatePosition],
  );

  useEffect(function cleanupTimerOnUnmount() {
    return () => clearCloseTimer();
  }, []);

  const tooltipContent = open && (
    <div
      ref={tooltipRef}
      className={cn("fixed z-[10200] background-neutral-fill radius-sm p-3", isPositioned && "animate-fadeIn")}
      style={{
        top: position ? `${position.top}px` : 0,
        left: position ? `${position.left}px` : 0,
        visibility: isPositioned ? "visible" : "hidden",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      {position && (
        <div
          className="absolute w-0 h-0"
          style={
            position.effectiveDirection === "bottom"
              ? {
                  top: `-${ARROW_SIZE - 1}px`,
                  left: `${position.arrowLeft}px`,
                  transform: "translateX(-50%)",
                  borderLeft: `${ARROW_SIZE}px solid transparent`,
                  borderRight: `${ARROW_SIZE}px solid transparent`,
                  borderBottom: `${ARROW_SIZE}px solid var(--color-grey-500)`,
                }
              : {
                  bottom: `-${ARROW_SIZE - 1}px`,
                  left: `${position.arrowLeft}px`,
                  transform: "translateX(-50%)",
                  borderLeft: `${ARROW_SIZE}px solid transparent`,
                  borderRight: `${ARROW_SIZE}px solid transparent`,
                  borderTop: `${ARROW_SIZE}px solid var(--color-grey-500)`,
                }
          }
        />
      )}
      {content}
    </div>
  );

  return (
    <>
      <div ref={triggerRef} className={cn(className ?? "inline-flex")} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </div>
      {typeof document !== "undefined" && tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  );
}
